/**
 * useCrossTabDiffToast — pops a subtle "N packages updated" toast on
 * the current tab whenever a sibling tab (or a bulk-apply / import)
 * dispatches the global `mci-register-updated` event. Suppresses toasts
 * fired by the current tab itself when a `source` is supplied.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

export default function useCrossTabDiffToast(tabId, { message = 'packages updated', min = 1 } = {}) {
  const lastFired = useRef(0);
  useEffect(() => {
    const handler = (ev) => {
      const src = ev.detail?.source;
      const count = ev.detail?.count ?? 1;
      // Skip toast if THIS tab was the origin
      if (src === tabId) return;
      // Debounce — same event fires from multiple listeners
      const now = Date.now();
      if (now - lastFired.current < 700) return;
      lastFired.current = now;
      if (count < min) return;
      toast.info(`${count === 1 ? 'A package was' : `${count} packages`} ${message}`,
        { duration: 3500, position: 'bottom-right' });
    };
    window.addEventListener('mci-register-updated', handler);
    return () => window.removeEventListener('mci-register-updated', handler);
  }, [tabId, message, min]);
}
